const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "")
const TENANT_STORAGE_KEY = "wl_public_rating_tenant_id"

const publicRatingHeaders = (language, tenantId) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Accept-Language": language === "sa" ? "ar" : "en",
  }
  if (tenantId) {
    headers["X-Tenant-ID"] = String(tenantId)
  }
  return headers
}

const parseJsonSafe = async response => {
  try {
    return await response.json()
  } catch (_err) {
    return null
  }
}

const readStoredTenantId = () => {
  if (typeof window === "undefined") {
    return ""
  }
  try {
    return String(window.sessionStorage.getItem(TENANT_STORAGE_KEY) || "").trim()
  } catch (_err) {
    return ""
  }
}

const persistTenantId = tenantId => {
  if (typeof window === "undefined" || !tenantId) {
    return
  }
  try {
    window.sessionStorage.setItem(TENANT_STORAGE_KEY, tenantId)
  } catch (_err) {
    // Ignore storage failures (private mode); in-memory callers still have tenantId.
  }
}

const stripTenantIdFromUrl = () => {
  if (typeof window === "undefined") {
    return
  }
  const url = new URL(window.location.href)
  if (!url.searchParams.has("tenantId") && !url.searchParams.has("tenant_id")) {
    return
  }
  url.searchParams.delete("tenantId")
  url.searchParams.delete("tenant_id")
  const nextSearch = url.searchParams.toString()
  const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash}`
  window.history.replaceState(null, "", nextUrl)
}

/**
 * Read tenantId from the scanned QR URL, keep it in sessionStorage, then
 * remove it from the visible browser URL.
 */
export const capturePublicRatingTenantId = () => {
  if (typeof window === "undefined") {
    return ""
  }

  const params = new URLSearchParams(window.location.search)
  const fromQuery = String(
    params.get("tenantId") || params.get("tenant_id") || ""
  ).trim()

  if (fromQuery) {
    persistTenantId(fromQuery)
    stripTenantIdFromUrl()
    return fromQuery
  }

  return readStoredTenantId()
}

const withTenantQuery = (path, tenantId) => {
  if (!tenantId) {
    return path
  }
  const separator = path.includes("?") ? "&" : "?"
  return `${path}${separator}tenantId=${encodeURIComponent(tenantId)}`
}

export const fetchPublicRatingPage = async (branchId, language, tenantId) => {
  const response = await fetch(
    withTenantQuery(
      `${API_BASE}/public/rating/${encodeURIComponent(branchId)}`,
      tenantId
    ),
    {
      method: "GET",
      headers: publicRatingHeaders(language, tenantId),
    }
  )
  const body = await parseJsonSafe(response)
  if (!response.ok || !body?.status) {
    const error = new Error(body?.msg || "Unable to load rating page")
    error.statusCode = body?.statusCode || response.status
    throw error
  }
  return body.data
}

export const submitPublicRating = async (payload, language, tenantId) => {
  const response = await fetch(
    withTenantQuery(`${API_BASE}/public/rating/save`, tenantId),
    {
      method: "POST",
      headers: publicRatingHeaders(language, tenantId),
      body: JSON.stringify({ ...payload, tenantId }),
    }
  )
  const body = await parseJsonSafe(response)
  if (!response.ok || !body?.status) {
    const error = new Error(body?.msg || "Unable to submit rating")
    error.statusCode = body?.statusCode || response.status
    throw error
  }
  return body.data
}
